import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, Card, Button, Alert, LoadingState } from '../components/ui';
import blacklistService from '../services/blacklistService';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const Blacklist = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [newWord, setNewWord] = useState('');
  const [bulkWords, setBulkWords] = useState('');
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  
  const queryClient = useQueryClient();
  
  // Fetch blacklist words
  const { data: blacklistWords = [], isLoading } = useQuery(
    'blacklist', 
    blacklistService.getBlacklistWords
  );
  
  // Add word mutation
  const addWordMutation = useMutation(
    (word) => blacklistService.addBlacklistWord(word),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blacklist');
        setNewWord('');
      },
      onError: (err) => {
        setError(`Failed to add word: ${err.message}`);
      }
    }
  );
  
  // Add bulk words mutation
  const addBulkWordsMutation = useMutation(
    (words) => blacklistService.addBulkBlacklistWords(words),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blacklist');
        setBulkWords('');
        setShowBulkAddModal(false);
      },
      onError: (err) => {
        setError(`Failed to add words: ${err.message}`);
      }
    }
  );
  
  // Delete word mutation
  const deleteWordMutation = useMutation(
    (id) => blacklistService.deleteBlacklistWord(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('blacklist');
      },
      onError: (err) => {
        setError(`Failed to delete word: ${err.message}`);
      }
    }
  );
  
  // Filter blacklist words based on search term
  const filteredWords = blacklistWords.filter(word => 
    word.word?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle add word
  const handleAddWord = (e) => {
    e.preventDefault();
    
    if (!newWord.trim()) {
      setError('Please enter a word to add');
      return;
    }
    
    addWordMutation.mutate(newWord.trim());
  };
  
  // Handle add bulk words
  const handleAddBulkWords = (e) => {
    e.preventDefault();
    
    if (!bulkWords.trim()) {
      setError('Please enter words to add');
      return;
    }
    
    // Split by newline, comma, or space and filter empty strings
    const words = bulkWords
      .split(/[\n,\s]+/)
      .map(word => word.trim())
      .filter(word => word);
    
    if (words.length === 0) {
      setError('No valid words found');
      return;
    }
    
    addBulkWordsMutation.mutate(words);
  };
  
  // Handle delete word
  const handleDeleteWord = (id) => {
    if (window.confirm('Are you sure you want to delete this word?')) {
      deleteWordMutation.mutate(id);
    }
  };
  
  // Handle test message
  const handleTestMessage = async (e) => {
    e.preventDefault();
    
    if (!testMessage.trim()) {
      setError('Please enter a message to test');
      return;
    }
    
    try {
      const result = await blacklistService.testMessage(testMessage);
      setTestResult(result);
    } catch (err) {
      setError(`Failed to test message: ${err.message}`);
    }
  };
  
  if (isLoading) {
    return <LoadingState message="Loading blacklist..." />;
  }
  
  return (
    <div>
      <PageHeader 
        title="Blacklist" 
        description="Manage blacklisted words and phrases"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={() => setShowBulkAddModal(true)}
            >
              Bulk Add
            </Button>
            <Button
              variant="primary"
              icon={PlusIcon}
              onClick={() => document.getElementById('newWordInput').focus()}
            >
              Add Word
            </Button>
          </div>
        }
      />
      
      {/* Bulk Add Modal */}
      {showBulkAddModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Add Multiple Words
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Enter multiple words separated by commas, spaces, or new lines.
                      </p>
                      
                      {error && addBulkWordsMutation.isError && (
                        <Alert 
                          variant="error" 
                          className="mt-4"
                          isDismissible
                          onDismiss={() => setError('')}
                        >
                          {error}
                        </Alert>
                      )}
                      
                      <form onSubmit={handleAddBulkWords} className="mt-4">
                        <textarea
                          rows={6}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          placeholder="word1, word2, word3&#10;word4 word5&#10;word6"
                          value={bulkWords}
                          onChange={(e) => setBulkWords(e.target.value)}
                          disabled={addBulkWordsMutation.isLoading}
                        />
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full sm:w-auto sm:ml-3"
                  onClick={handleAddBulkWords}
                  isLoading={addBulkWordsMutation.isLoading}
                  disabled={addBulkWordsMutation.isLoading}
                >
                  Add Words
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                  onClick={() => setShowBulkAddModal(false)}
                  disabled={addBulkWordsMutation.isLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Blacklist Words */}
        <Card title="Blacklisted Words">
          {error && addWordMutation.isError && (
            <Alert 
              variant="error" 
              className="mb-4"
              isDismissible
              onDismiss={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          <div className="mb-4">
            <form onSubmit={handleAddWord} className="flex">
              <input
                id="newWordInput"
                type="text"
                className="flex-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-l-md"
                placeholder="Add new word or phrase"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                disabled={addWordMutation.isLoading}
              />
              <Button
                type="submit"
                variant="primary"
                className="rounded-l-none"
                isLoading={addWordMutation.isLoading}
                disabled={addWordMutation.isLoading}
              >
                Add
              </Button>
            </form>
          </div>
          
          <div className="mb-4">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="search"
                name="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search blacklist"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-96">
            {filteredWords.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {filteredWords.map((word) => (
                  <li key={word.id} className="py-3 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900">{word.word}</span>
                    <button
                      type="button"
                      className="ml-2 text-red-600 hover:text-red-900"
                      onClick={() => handleDeleteWord(word.id)}
                      disabled={deleteWordMutation.isLoading}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-500 py-4">
                {searchTerm ? 'No matching words found' : 'No blacklisted words yet'}
              </p>
            )}
          </div>
          
          <div className="mt-4 text-sm text-gray-500">
            Total: {blacklistWords.length} words
          </div>
        </Card>
        
        {/* Test Message */}
        <Card title="Test Message">
          <p className="text-sm text-gray-500 mb-4">
            Test a message against the blacklist to see if it would be blocked.
          </p>
          
          <form onSubmit={handleTestMessage}>
            <div className="mb-4">
              <label htmlFor="testMessage" className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                id="testMessage"
                rows={4}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Enter a message to test"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
              />
            </div>
            
            <Button
              type="submit"
              variant="primary"
            >
              Test Message
            </Button>
          </form>
          
          {testResult && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Result:</h4>
              
              {testResult.blocked ? (
                <Alert variant="error">
                  <p>Message would be <strong>blocked</strong>.</p>
                  <p className="mt-1">
                    Matched words: {testResult.matches.join(', ')}
                  </p>
                </Alert>
              ) : (
                <Alert variant="success">
                  <p>Message would be <strong>allowed</strong>.</p>
                  <p className="mt-1">No blacklisted words found.</p>
                </Alert>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Blacklist;
